import { t } from "@lingui/macro";
import NavigateBeforeOutlinedIcon from "@mui/icons-material/NavigateBeforeOutlined";
import NavigateNextOutlinedIcon from "@mui/icons-material/NavigateNextOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link as MuiLink,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Rating,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  fetchStudentLmsQuizResults,
  fetchStudentLmsCourseFeedback,
  fetchLmsQuizById,
  LmsLesson,
  submitLmsCourseFeedback,
  submitLmsChapterQuiz,
  submitLmsFinalQuiz,
  useLmsCourseDetails,
  useUpdateLmsProgress,
} from "@/client/services/lms";
import { useUser } from "@/client/services/user";

const FINAL_QUIZ_CHAPTER_ID = "__final__";
const FEEDBACK_SUGGESTIONS = [
  "Content quality",
  "Instructor clarity",
  "Quiz quality",
  "Platform experience",
  "Need more examples",
];

const mapResourceDownloadUrl = (rawUrl: string) => {
  const value = String(rawUrl || "").trim();
  if (!value) return value;
  if (value.startsWith("/exam/api/lms/resources/")) {
    return value.replace("/exam/api/lms", "/api/integration/external-service/lms");
  }
  if (value.startsWith("/integration/external-service/lms/resources/")) {
    return `/api${value}`;
  }
  return value;
};

const downloadFile = async (rawUrl: string, filename?: string) => {
  const url = mapResourceDownloadUrl(rawUrl);
  if (!url) return;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      // Fallback: open in new tab if fetch fails
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    // Derive filename from URL path or use provided name
    const derivedName = filename || url.split("/").pop()?.split("?")[0] || "download";
    anchor.download = derivedName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(blobUrl);
  } catch {
    // Fallback: open in new tab
    window.open(url, "_blank", "noopener,noreferrer");
  }
};

type QuizResultView = {
  label: string;
  status: string;
  answersMap: Record<string, number>;
};

const toQuizLabel = (percentage: unknown) => {
  const parsed = Number(percentage);
  return Number.isFinite(parsed) ? `${parsed}%` : "Completed";
};

// const resolveYoutubeEmbed = (videoUrl?: string) => {
//   if (!videoUrl) return null;
//   const match = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
//   if (!match) return null;
//   return `https://www.youtube.com/embed/${match[1]}?rel=0&modestbranding=1`;
// };

const resolveYoutubeEmbed = (videoUrl?: string) => {
  if (!videoUrl) return null;
  const match = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (!match) return null;
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? `&origin=${encodeURIComponent(window.location.origin)}`
      : "";
  return `https://www.youtube.com/embed/${match[1]}?rel=0&modestbranding=1${origin}`;
};

const isExternalLesson = (lesson?: LmsLesson) => {
  if (!lesson) return false;
  return lesson.videoSourceType === "external";
};

const resolveMeetingProvider = (videoUrl?: string) => {
  const url = String(videoUrl || "").toLowerCase();
  if (url.includes("zoom.us")) return "zoom";
  if (url.includes("teams.microsoft.com")) return "teams";
  if (url.includes("meet.google.com")) return "google-meet";
  return "external";
};

const LmsPlayerPage = () => {
  const { courseId = "" } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: course, isLoading } = useLmsCourseDetails(courseId);
  const { user } = useUser();
  const updateProgress = useUpdateLmsProgress();

  const lessons = useMemo(
    () => course?.chapters.flatMap((chapter) => chapter.lessons) ?? [],
    [course?.chapters],
  );
  const [activeLessonId, setActiveLessonId] = useState<string>("");
  const [completedLessonIds, setCompletedLessonIds] = useState<string[]>([]);
  const [quizResultsByChapter, setQuizResultsByChapter] = useState<Record<string, QuizResultView>>(
    {},
  );
  const [quizDialogOpen, setQuizDialogOpen] = useState(false);
  const [activeQuizChapterId, setActiveQuizChapterId] = useState("");
  const [activeQuiz, setActiveQuiz] = useState<any>(null);
  const [resourcesDialogOpen, setResourcesDialogOpen] = useState(false);
  const [activeResourcesChapterId, setActiveResourcesChapterId] = useState("");
  const [activeResourcesTitle, setActiveResourcesTitle] = useState("");
  const [activeResources, setActiveResources] = useState<
    Array<{
      id: string;
      type: "url" | "text" | "file";
      title?: string;
      value: string;
    }>
  >([]);
  const [quizAnswers, setQuizAnswers] = useState<Record<string, number>>({});
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState<number | null>(0);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSuggestions, setFeedbackSuggestions] = useState<string[]>([]);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState<"submitted" | "skipped" | null>(null);
  const [feedbackReadOnly, setFeedbackReadOnly] = useState(false);

  useEffect(() => {
    const run = async () => {
      if (!courseId || !user?.email || !user?.id) return;
      try {
        const rows = await fetchStudentLmsQuizResults(courseId);
        const mapped: Record<string, QuizResultView> = {};
        rows.forEach((row) => {
          const rawAnswers = Array.isArray((row.rawResult as any)?.answers)
            ? (row.rawResult as any).answers
            : [];
          const answersMap: Record<string, number> = {};
          rawAnswers.forEach((answer: any) => {
            const qid = String(answer?.questionId || "").trim();
            if (!qid) return;
            answersMap[qid] = Number(answer?.selectedOptionIndex ?? -1);
          });
          mapped[row.chapterId] = {
            label: toQuizLabel(row.percentage),
            status: String(row.status || ""),
            answersMap,
          };
        });
        setQuizResultsByChapter(mapped);
      } catch {
        setQuizResultsByChapter({});
      }
    };
    run();
  }, [courseId, user?.email, user?.id]);

  useEffect(() => {
    const run = async () => {
      if (!courseId || !user?.id) return;
      try {
        const existing = await fetchStudentLmsCourseFeedback(courseId);
        if (!existing) return;
        setFeedbackStatus(existing.status === "skipped" ? "skipped" : "submitted");
        setFeedbackRating(Number(existing.rating ?? 0));
        setFeedbackText(String(existing.feedback || ""));
        setFeedbackSuggestions(Array.isArray(existing.suggestions) ? existing.suggestions : []);
      } catch {
        // Ignore feedback fetch errors on player load.
      }
    };
    run();
  }, [courseId, user?.id]);

  useEffect(() => {
    const rows = (course?.completedLessonIds || []).map((id) => String(id));
    setCompletedLessonIds(rows);
  }, [course?.completedLessonIds]);

  const completedSet = useMemo(() => new Set(completedLessonIds), [completedLessonIds]);
  const requestedLessonId = useMemo(
    () => String(searchParams.get("lessonId") || "").trim(),
    [searchParams],
  );

  const chapterFlow = useMemo(() => {
    const chapters = course?.chapters || [];
    const lessonState: Array<{
      lesson: LmsLesson;
      chapterId: string;
      chapterIndex: number;
      lessonIndex: number;
      unlocked: boolean;
      completed: boolean;
    }> = [];
    const chapterState: Array<{
      id: string;
      unlocked: boolean;
      lessonsCompleted: boolean;
      quizRequired: boolean;
      quizCompleted: boolean;
      completed: boolean;
      quizUnlocked: boolean;
    }> = [];

    let allPreviousChaptersCompleted = true;
    chapters.forEach((chapter, chapterIndex) => {
      const lessons = chapter.lessons || [];
      const lessonsCompleted = lessons.every((lesson) => completedSet.has(String(lesson.id)));
      const quizRequired = Boolean(chapter.quizEnabled && chapter.quizId);
      const quizCompleted =
        String(quizResultsByChapter[chapter.id]?.status || "").toLowerCase() === "completed";
      const completed = lessonsCompleted && (!quizRequired || quizCompleted);
      const unlocked = chapterIndex === 0 ? true : allPreviousChaptersCompleted;
      const quizUnlocked = unlocked && lessonsCompleted;

      lessons.forEach((lesson, lessonIndex) => {
        const previousLessonId = lessonIndex > 0 ? lessons[lessonIndex - 1]?.id : "";
        const lessonUnlocked =
          unlocked && (lessonIndex === 0 || completedSet.has(String(previousLessonId)));
        lessonState.push({
          lesson,
          chapterId: chapter.id,
          chapterIndex,
          lessonIndex,
          unlocked: lessonUnlocked,
          completed: completedSet.has(String(lesson.id)),
        });
      });

      chapterState.push({
        id: chapter.id,
        unlocked,
        lessonsCompleted,
        quizRequired,
        quizCompleted,
        completed,
        quizUnlocked,
      });

      allPreviousChaptersCompleted = allPreviousChaptersCompleted && completed;
    });

    const allChaptersCompleted = chapterState.every((chapter) => chapter.completed);
    const finalQuizRequired = Boolean(course?.finalQuizEnabled && course?.finalQuizId);
    const finalQuizCompleted =
      String(quizResultsByChapter[FINAL_QUIZ_CHAPTER_ID]?.status || "").toLowerCase() ===
      "completed";
    const finalQuizUnlocked = allChaptersCompleted;
    const courseCompleted = allChaptersCompleted && (!finalQuizRequired || finalQuizCompleted);

    return {
      lessonState,
      chapterState,
      allChaptersCompleted,
      finalQuizRequired,
      finalQuizCompleted,
      finalQuizUnlocked,
      courseCompleted,
    };
  }, [
    completedSet,
    course?.chapters,
    course?.finalQuizEnabled,
    course?.finalQuizId,
    quizResultsByChapter,
  ]);

  const lessonStateMap = useMemo(() => {
    const map = new Map<string, (typeof chapterFlow.lessonState)[number]>();
    chapterFlow.lessonState.forEach((item) => map.set(String(item.lesson.id), item));
    return map;
  }, [chapterFlow.lessonState]);

  const activeLesson = useMemo(() => {
    if (chapterFlow.lessonState.length === 0) return undefined;
    const found = chapterFlow.lessonState.find(
      (item) => String(item.lesson.id) === activeLessonId && item.unlocked,
    );
    if (found) return found.lesson;
    return (
      chapterFlow.lessonState.find((item) => item.unlocked)?.lesson ??
      chapterFlow.lessonState[0].lesson
    );
  }, [activeLessonId, chapterFlow.lessonState]);

  useEffect(() => {
    if (!requestedLessonId) return;
    const canOpenRequestedLesson = chapterFlow.lessonState.some(
      (item) => String(item.lesson.id) === requestedLessonId && item.unlocked,
    );
    if (!canOpenRequestedLesson) return;
    setActiveLessonId((prev) => (prev ? prev : requestedLessonId));
  }, [chapterFlow.lessonState, requestedLessonId]);

  const activeIndex = useMemo(
    () =>
      chapterFlow.lessonState.findIndex(
        (item) => String(item.lesson.id) === String(activeLesson?.id),
      ),
    [activeLesson?.id, chapterFlow.lessonState],
  );
  const prevLesson = activeIndex > 0 ? chapterFlow.lessonState[activeIndex - 1]?.lesson : undefined;
  const nextLessonState = activeIndex >= 0 ? chapterFlow.lessonState[activeIndex + 1] : undefined;
  const nextLesson = nextLessonState?.unlocked ? nextLessonState.lesson : undefined;
  const activeLessonState = activeLesson ? lessonStateMap.get(String(activeLesson.id)) : undefined;

  const selectLesson = (lesson: LmsLesson) => {
    const state = lessonStateMap.get(String(lesson.id));
    if (!state?.unlocked) return;
    setActiveLessonId(lesson.id);
  };

  const markComplete = async () => {
    if (!activeLesson || !course) return;
    await updateProgress.mutateAsync({
      courseId: course.id,
      lessonId: activeLesson.id,
      watchedPercent: 100,
    });
    setCompletedLessonIds((prev) =>
      prev.includes(activeLesson.id) ? prev : [...prev, activeLesson.id],
    );
    await handleNext();
  };

  const handleNext = async () => {
    if (!course || !activeLesson || !activeLessonState) return;
    const chapterIndex = activeLessonState.chapterIndex;
    const chapter = course.chapters[chapterIndex];
    if (!chapter) return;

    const isLastLessonInChapter =
      activeLessonState.lessonIndex >= (chapter.lessons?.length || 0) - 1;
    if (!isLastLessonInChapter) {
      if (nextLesson) {
        selectLesson(nextLesson);
      }
      return;
    }

    if (
      chapter.resourcesEnabled &&
      (chapter.resources || []).some((item) => String(item.value || "").trim())
    ) {
      openChapterResources(chapter.id);
      return;
    }

    if (chapter.quizEnabled && chapter.quizId) {
      await openChapterQuiz(chapter.id, chapter.quizId);
      return;
    }

    const nextChapter = course.chapters[chapterIndex + 1];
    if (nextChapter?.lessons?.length) {
      selectLesson(nextChapter.lessons[0]);
      return;
    }

    if (course.finalQuizEnabled && course.finalQuizId) {
      await openChapterQuiz(FINAL_QUIZ_CHAPTER_ID, course.finalQuizId);
    }
  };
  const moveForwardFromChapterQuiz = async (chapterId: string) => {
    if (!course) return;
    const chapterIndex = (course.chapters || []).findIndex(
      (chapter) => String(chapter.id) === String(chapterId),
    );
    if (chapterIndex < 0) return;
    const nextChapter = course.chapters[chapterIndex + 1];
    if (nextChapter?.lessons?.length) {
      setActiveLessonId(String(nextChapter.lessons[0].id));
      return;
    }
    if (course.finalQuizEnabled && course.finalQuizId) {
      await openChapterQuiz(FINAL_QUIZ_CHAPTER_ID, course.finalQuizId);
    }
  };

  const moveForwardFromChapterResources = async (chapterId: string) => {
    if (!course) return;
    const chapterIndex = (course.chapters || []).findIndex(
      (chapter) => String(chapter.id) === String(chapterId),
    );
    if (chapterIndex < 0) return;
    const chapter = course.chapters[chapterIndex];
    if (chapter?.quizEnabled && chapter.quizId) {
      await openChapterQuiz(chapter.id, chapter.quizId);
      return;
    }
    const nextChapter = course.chapters[chapterIndex + 1];
    if (nextChapter?.lessons?.length) {
      setActiveLessonId(String(nextChapter.lessons[0].id));
      return;
    }
    if (course.finalQuizEnabled && course.finalQuizId) {
      await openChapterQuiz(FINAL_QUIZ_CHAPTER_ID, course.finalQuizId);
    }
  };

  if (isLoading) {
    return (
      <Stack alignItems="center" py={8}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!course || !activeLesson) {
    return (
      <Container>
        <Typography>{t`Unable to load the course player.`}</Typography>
      </Container>
    );
  }

  const youtubeEmbedUrl = resolveYoutubeEmbed(activeLesson.videoUrl);
  const externalLesson = isExternalLesson(activeLesson);
  const provider = resolveMeetingProvider(activeLesson.videoUrl);
  const providerTitle =
    provider === "zoom"
      ? t`Zoom Meeting`
      : provider === "teams"
        ? t`Microsoft Teams Meeting`
        : provider === "google-meet"
          ? t`Google Meet`
          : t`Live Class Link`;
  const providerCta =
    provider === "zoom"
      ? t`Join Zoom Meeting`
      : provider === "teams"
        ? t`Join Teams Meeting`
        : provider === "google-meet"
          ? t`Join Google Meet`
          : t`Join Live Class`;
  const isActiveLessonCompleted = completedLessonIds.includes(activeLesson.id);

  const openChapterQuiz = async (chapterId: string, quizId: string) => {
    if (!quizId) return;
    const quiz = await fetchLmsQuizById(quizId);
    setActiveQuizChapterId(chapterId);
    setActiveQuiz(quiz);
    const savedAnswers = quizResultsByChapter[chapterId]?.answersMap || {};
    setQuizAnswers(savedAnswers);
    setQuizDialogOpen(true);
  };

  const openChapterResources = (chapterId: string) => {
    const chapter = (course?.chapters || []).find((item) => String(item.id) === String(chapterId));
    if (!chapter || !chapter.resourcesEnabled) return;
    const resources = (chapter.resources || []).filter((item) => String(item.value || "").trim());
    if (resources.length === 0) return;

    setActiveResourcesChapterId(String(chapter.id));
    setActiveResourcesTitle(String(chapter.resourcesTitle || chapter.title || "").trim());
    setActiveResources(resources);
    setResourcesDialogOpen(true);
  };

  const submitQuiz = async () => {
    if (!course || !activeQuizChapterId || !activeQuiz) return;
    if (
      String(quizResultsByChapter[activeQuizChapterId]?.status || "").toLowerCase() === "completed"
    ) {
      setQuizDialogOpen(false);
      return;
    }
    const answers = (activeQuiz.questions || []).map((question: any) => ({
      questionId: question.id,
      selectedOptionIndex: Number(quizAnswers[question.id] ?? -1),
    }));
    setSubmittingQuiz(true);
    try {
      if (activeQuizChapterId === FINAL_QUIZ_CHAPTER_ID) {
        await submitLmsFinalQuiz({
          courseId: course.id,
          answers,
        });
      } else {
        await submitLmsChapterQuiz({
          courseId: course.id,
          chapterId: activeQuizChapterId,
          answers,
        });
      }
      const rows = await fetchStudentLmsQuizResults(course.id);
      const mapped: Record<string, QuizResultView> = {};
      rows.forEach((row) => {
        const rawAnswers = Array.isArray((row.rawResult as any)?.answers)
          ? (row.rawResult as any).answers
          : [];
        const answersMap: Record<string, number> = {};
        rawAnswers.forEach((answer: any) => {
          const qid = String(answer?.questionId || "").trim();
          if (!qid) return;
          answersMap[qid] = Number(answer?.selectedOptionIndex ?? -1);
        });
        mapped[row.chapterId] = {
          label: toQuizLabel(row.percentage),
          status: String(row.status || ""),
          answersMap,
        };
      });
      setQuizResultsByChapter(mapped);
      setQuizDialogOpen(false);
      if (activeQuizChapterId === FINAL_QUIZ_CHAPTER_ID && !feedbackStatus) {
        setFeedbackReadOnly(false);
        setFeedbackDialogOpen(true);
      }

      if (activeQuizChapterId !== FINAL_QUIZ_CHAPTER_ID) {
        const chapterIndex = (course.chapters || []).findIndex(
          (chapter) => String(chapter.id) === String(activeQuizChapterId),
        );
        const nextChapter = chapterIndex >= 0 ? course.chapters[chapterIndex + 1] : undefined;
        if (nextChapter?.lessons?.length) {
          setActiveLessonId(String(nextChapter.lessons[0].id));
        } else if (course.finalQuizEnabled && course.finalQuizId) {
          await openChapterQuiz(FINAL_QUIZ_CHAPTER_ID, course.finalQuizId);
        }
      }
    } finally {
      setSubmittingQuiz(false);
    }
  };
  const activeQuizCompleted =
    String(quizResultsByChapter[activeQuizChapterId]?.status || "").toLowerCase() === "completed";
  const canGoNextFromQuizPopup = (() => {
    if (!activeQuizCompleted) return false;
    if (!course || activeQuizChapterId === FINAL_QUIZ_CHAPTER_ID) return false;
    const chapterIndex = (course.chapters || []).findIndex(
      (chapter) => String(chapter.id) === String(activeQuizChapterId),
    );
    if (chapterIndex < 0) return false;
    const hasNextChapterLesson = Boolean(course.chapters[chapterIndex + 1]?.lessons?.length);
    const hasFinalQuiz = Boolean(course.finalQuizEnabled && course.finalQuizId);
    return hasNextChapterLesson || hasFinalQuiz;
  })();
  const canGoNextFromResourcesPopup = (() => {
    if (!course || !activeResourcesChapterId) return false;
    const chapterIndex = (course.chapters || []).findIndex(
      (chapter) => String(chapter.id) === String(activeResourcesChapterId),
    );
    if (chapterIndex < 0) return false;
    const hasChapterQuiz = Boolean(
      course.chapters[chapterIndex]?.quizEnabled && course.chapters[chapterIndex]?.quizId,
    );
    const hasNextChapterLesson = Boolean(course.chapters[chapterIndex + 1]?.lessons?.length);
    const hasFinalQuiz = Boolean(course.finalQuizEnabled && course.finalQuizId);
    return hasChapterQuiz || hasNextChapterLesson || hasFinalQuiz;
  })();

  const toggleFeedbackSuggestion = (value: string) => {
    setFeedbackSuggestions((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value],
    );
  };

  const submitCourseFeedback = async () => {
    if (!course) return;
    if (feedbackStatus === "submitted") return;
    const rating = Number(feedbackRating ?? 0);
    if (rating < 1 || rating > 5) return;

    setFeedbackSubmitting(true);
    try {
      await submitLmsCourseFeedback({
        courseId: course.id,
        status: "submitted",
        rating,
        feedback: feedbackText,
        suggestions: feedbackSuggestions,
      });
      setFeedbackStatus("submitted");
      setFeedbackReadOnly(true);
      setFeedbackDialogOpen(false);
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const skipCourseFeedback = async () => {
    if (!course) return;
    if (feedbackStatus === "submitted") {
      setFeedbackDialogOpen(false);
      return;
    }
    setFeedbackSubmitting(true);
    try {
      await submitLmsCourseFeedback({
        courseId: course.id,
        status: "skipped",
        rating: 0,
        feedback: "",
        suggestions: [],
      });
      setFeedbackStatus("skipped");
      setFeedbackDialogOpen(false);
    } finally {
      setFeedbackSubmitting(false);
    }
  };
  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/lms");
  };

  return (
    <Box>
      <Container maxWidth={false}>
        <Stack spacing={2}>
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              size="small"
              onClick={handleGoBack}
              sx={{ textTransform: "none" }}
            >
              {t`Back`}
            </Button>
          </Box>
          <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Stack spacing={2}>
                <Box
                  sx={{
                    position: { xs: "static", lg: "sticky" },
                    top: { lg: 56 },
                    zIndex: 4,
                    bgcolor: "background.paper",
                    borderRadius: 2,
                    mb: 1,
                  }}
                >
                  {youtubeEmbedUrl ? (
                    <Box
                      component="iframe"
                      src={youtubeEmbedUrl}
                      title={activeLesson.title}
                      sx={{
                        width: "100%",
                        aspectRatio: "16 / 9",
                        border: 0,
                        borderRadius: 2,
                        bgcolor: "black",
                      }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : externalLesson ? (
                    <Stack
                      sx={{
                        width: "100%",
                        aspectRatio: "16 / 9",
                        borderRadius: 2,
                        bgcolor: "#0f172a",
                        color: "#f8fafc",
                        p: 3,
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                      spacing={1.5}
                    >
                      <Typography variant="h6" fontWeight={700}>
                        {providerTitle}
                      </Typography>
                      <Typography color="rgba(248,250,252,0.85)">
                        {t`This lesson uses an external meeting link.`}
                      </Typography>
                      <Button
                        component={MuiLink}
                        href={activeLesson.videoUrl || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="contained"
                        color="success"
                        disabled={!activeLesson.videoUrl}
                      >
                        {providerCta}
                      </Button>
                    </Stack>
                  ) : (
                    <Box
                      component="video"
                      src={activeLesson.videoUrl}
                      controls
                      controlsList="nodownload"
                      sx={{
                        width: "100%",
                        aspectRatio: "16 / 9",
                        borderRadius: 2,
                        bgcolor: "black",
                      }}
                    />
                  )}
                </Box>

                <Stack spacing={1} sx={{ position: "relative", zIndex: 2, pt: 0.5 }}>
                  <Typography variant="h6" fontWeight={700}>
                    {activeLesson.title}
                  </Typography>
                  <Typography color="text.secondary">
                    {/* {activeLesson.description || t`Lesson details can be added from the admin builder.`} */}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    startIcon={<NavigateBeforeOutlinedIcon />}
                    disabled={!prevLesson}
                    onClick={() => prevLesson && selectLesson(prevLesson)}
                  >
                    {t`Previous`}
                  </Button>
                  <Button
                    variant="outlined"
                    endIcon={<NavigateNextOutlinedIcon />}
                    disabled={!activeLessonState || !isActiveLessonCompleted}
                    onClick={handleNext}
                  >
                    {t`Next`}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<TaskAltOutlinedIcon />}
                    onClick={markComplete}
                    disabled={updateProgress.isPending || isActiveLessonCompleted}
                  >
                    {updateProgress.isPending
                      ? t`Updating...`
                      : isActiveLessonCompleted
                        ? t`Completed`
                        : t`Mark Complete`}
                  </Button>
                  <Button component={Link} to={`/lms/${course.id}`}>
                    {t`Course Overview`}
                  </Button>
                </Stack>
              </Stack>
            </Box>

            <Paper
              variant="outlined"
              sx={{
                width: { xs: "100%", lg: 360 },
                maxHeight: { lg: "calc(100vh - 95px)" },
                overflow: "auto",
                borderRadius: 2,
              }}
            >
              {course.chapters.map((chapter, chapterIndex) => {
                const chapterStatus = chapterFlow.chapterState[chapterIndex];
                const chapterLocked = !chapterStatus?.unlocked;
                return (
                  <Box key={chapter.id}>
                    <Stack sx={{ px: 2, py: 1.2, bgcolor: "#f8fafc" }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: chapterLocked ? "text.disabled" : "text.primary",
                        }}
                      >
                        {chapter.title}
                      </Typography>
                    </Stack>
                    <List disablePadding>
                      {chapter.lessons.map((lesson) =>
                        (() => {
                          const lessonState = lessonStateMap.get(String(lesson.id));
                          return (
                            <ListItemButton
                              key={lesson.id}
                              selected={lesson.id === activeLesson.id}
                              disabled={!lessonState?.unlocked}
                              onClick={() => selectLesson(lesson)}
                            >
                              <ListItemText
                                primary={lesson.title}
                                secondary={
                                  lessonState?.completed
                                    ? `${lesson.durationInMinutes ?? 0} ${t`mins`} | ${t`Completed`}`
                                    : lessonState?.unlocked
                                      ? `${lesson.durationInMinutes ?? 0} ${t`mins`}`
                                      : t`Locked`
                                }
                                primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
                              />
                            </ListItemButton>
                          );
                        })(),
                      )}
                      {chapter.resourcesEnabled &&
                      (chapter.resources || []).some((item) => String(item.value || "").trim()) ? (
                        <ListItemButton
                          disabled={!chapterStatus?.quizUnlocked}
                          onClick={() => openChapterResources(chapter.id)}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            width="100%"
                          >
                            <ListItemText
                              primary={`${chapter.title} - ${chapter.resourcesTitle || t`Resources`}`}
                              secondary={chapterStatus?.quizUnlocked ? t`Available` : t`Locked`}
                              primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
                            />
                          </Stack>
                        </ListItemButton>
                      ) : null}
                      {chapter.quizEnabled && chapter.quizId ? (
                        <ListItemButton
                          disabled={!chapterStatus?.quizUnlocked}
                          onClick={() => openChapterQuiz(chapter.id, chapter.quizId || "")}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            width="100%"
                          >
                            <ListItemText
                              primary={`${chapter.title} - ${t`Quiz`}`}
                              secondary={
                                quizResultsByChapter[chapter.id]
                                  ? `${t`Completed`}: ${quizResultsByChapter[chapter.id].label}`
                                  : chapterStatus?.quizUnlocked
                                    ? t`Pending`
                                    : t`Locked`
                              }
                              primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
                            />
                            {String(
                              quizResultsByChapter[chapter.id]?.status || "",
                            ).toLowerCase() === "completed" ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  moveForwardFromChapterQuiz(chapter.id);
                                }}
                              >
                                {t`Next`}
                              </Button>
                            ) : null}
                          </Stack>
                        </ListItemButton>
                      ) : null}
                    </List>
                  </Box>
                );
              })}
              {course.finalQuizEnabled && course.finalQuizId ? (
                <Box sx={{ borderTop: "1px solid #e2e8f0", p: 2 }}>
                  <List disablePadding>
                    <ListItemButton
                      disabled={!chapterFlow.finalQuizUnlocked}
                      onClick={() =>
                        openChapterQuiz(FINAL_QUIZ_CHAPTER_ID, course.finalQuizId || "")
                      }
                    >
                      <ListItemText
                        primary={course.finalQuizTitle || t`Final Quiz`}
                        secondary={
                          quizResultsByChapter[FINAL_QUIZ_CHAPTER_ID]
                            ? `${t`Completed`}: ${quizResultsByChapter[FINAL_QUIZ_CHAPTER_ID].label}`
                            : chapterFlow.finalQuizUnlocked
                              ? t`Pending`
                              : t`Locked`
                        }
                        primaryTypographyProps={{ fontSize: 14, fontWeight: 700 }}
                      />
                    </ListItemButton>
                  </List>
                </Box>
              ) : null}
              <Box sx={{ p: 2, borderTop: "1px solid #e2e8f0" }}>
                <Typography
                  variant="caption"
                  color={chapterFlow.courseCompleted ? "success.main" : "text.secondary"}
                >
                  {chapterFlow.courseCompleted
                    ? t`Course completed`
                    : t`Complete all lessons and required quizzes to unlock all content`}
                </Typography>
              </Box>
            </Paper>
          </Stack>
        </Stack>
      </Container>

      <Dialog
        open={quizDialogOpen}
        onClose={() => setQuizDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{activeQuiz?.title || t`Chapter Quiz`}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {(activeQuiz?.questions || []).map((question: any, index: number) => (
              <Paper key={question.id} variant="outlined" sx={{ p: 1.5 }}>
                <Typography fontWeight={600} mb={1}>
                  {index + 1}. {question.question}
                </Typography>
                <TextField
                  select
                  fullWidth
                  label={t`Select Answer`}
                  value={quizAnswers[question.id] ?? ""}
                  disabled={activeQuizCompleted}
                  onChange={(event) =>
                    setQuizAnswers((prev) => ({
                      ...prev,
                      [question.id]: Number(event.target.value),
                    }))
                  }
                >
                  {question.options.map((option: string, optionIndex: number) => (
                    <MenuItem key={`${question.id}-${optionIndex}`} value={optionIndex}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuizDialogOpen(false)}>{t`Cancel`}</Button>
          {!activeQuizCompleted ? (
            <Button onClick={submitQuiz} variant="contained" disabled={submittingQuiz}>
              {submittingQuiz ? t`Submitting...` : t`Submit Quiz`}
            </Button>
          ) : (
            <Stack direction="row" spacing={1}>
              <Button variant="contained" disabled>
                {t`Quiz Completed`}
              </Button>
              {canGoNextFromQuizPopup ? (
                <Button
                  variant="outlined"
                  onClick={async () => {
                    setQuizDialogOpen(false);
                    await moveForwardFromChapterQuiz(activeQuizChapterId);
                  }}
                >
                  {t`Next`}
                </Button>
              ) : null}
            </Stack>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={resourcesDialogOpen}
        onClose={() => setResourcesDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{activeResourcesTitle || t`Chapter Resources`}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {activeResources.map((resource) => (
              <Paper key={resource.id} variant="outlined" sx={{ p: 1.5 }}>
                <Stack spacing={1}>
                  <Typography fontWeight={700}>
                    {resource.title ||
                      (resource.type === "url"
                        ? t`Resource Link`
                        : resource.type === "file"
                          ? t`Resource File`
                          : t`Resource Note`)}
                  </Typography>
                  {resource.type === "file" ||
                  (resource.type === "text" &&
                    /^https?:\/\//i.test(String(resource.value || "").trim())) ? (
                    <Stack spacing={1}>
                      {String(resource.value || "")
                        .split("\n")
                        .map((entry) => entry.trim())
                        .filter(Boolean)
                        .map((fileUrl, index) => (
                          <Button
                            key={`${resource.id}-file-${index}`}
                            variant="outlined"
                            sx={{ alignSelf: "flex-start" }}
                            onClick={() => downloadFile(fileUrl)}
                          >
                            {t`Download file`} {index + 1}
                          </Button>
                        ))}
                    </Stack>
                  ) : resource.type === "url" ? (
                    <Button
                      component={MuiLink}
                      href={resource.value}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      sx={{ alignSelf: "flex-start" }}
                    >
                      {resource.value}
                    </Button>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {resource.value}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            ))}
            {activeResources.length === 0 ? (
              <Typography color="text.secondary">{t`No resources available.`}</Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResourcesDialogOpen(false)}>{t`Close`}</Button>
          {canGoNextFromResourcesPopup ? (
            <Button
              variant="contained"
              onClick={async () => {
                setResourcesDialogOpen(false);
                await moveForwardFromChapterResources(activeResourcesChapterId);
              }}
            >
              {t`Next`}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog
        open={feedbackDialogOpen}
        onClose={() => setFeedbackDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t`Rate this course`}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack spacing={0.5}>
              <Typography variant="body2">{t`Rating`}</Typography>
              <Rating
                value={feedbackRating}
                onChange={(_, value) => setFeedbackRating(value)}
                max={5}
                readOnly={feedbackReadOnly}
              />
            </Stack>

            <Stack spacing={1}>
              <Typography variant="body2">{t`What went well?`}</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {FEEDBACK_SUGGESTIONS.map((item) => (
                  <Chip
                    key={item}
                    label={item}
                    clickable
                    disabled={feedbackReadOnly}
                    color={feedbackSuggestions.includes(item) ? "primary" : "default"}
                    variant={feedbackSuggestions.includes(item) ? "filled" : "outlined"}
                    onClick={() => toggleFeedbackSuggestion(item)}
                  />
                ))}
              </Stack>
            </Stack>

            <TextField
              multiline
              minRows={4}
              label={t`Feedback`}
              value={feedbackText}
              disabled={feedbackReadOnly}
              onChange={(event) => setFeedbackText(event.target.value)}
              placeholder={t`Share your feedback or suggestions`}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          {feedbackReadOnly ? (
            <Button onClick={() => setFeedbackDialogOpen(false)}>{t`Close`}</Button>
          ) : (
            <>
              <Button onClick={skipCourseFeedback} disabled={feedbackSubmitting}>{t`Skip`}</Button>
              <Button
                variant="contained"
                onClick={submitCourseFeedback}
                disabled={feedbackSubmitting || Number(feedbackRating ?? 0) < 1}
              >
                {feedbackSubmitting ? t`Submitting...` : t`Submit Feedback`}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LmsPlayerPage;



// import { t } from "@lingui/macro";
// import NavigateBeforeOutlinedIcon from "@mui/icons-material/NavigateBeforeOutlined";
// import NavigateNextOutlinedIcon from "@mui/icons-material/NavigateNextOutlined";
// import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
// import {
//   Box,
//   Button,
//   Chip,
//   CircularProgress,
//   Container,
//   Dialog,
//   DialogActions,
//   DialogContent,
//   DialogTitle,
//   Link as MuiLink,
//   List,
//   ListItemButton,
//   ListItemText,
//   MenuItem,
//   Paper,
//   Rating,
//   Stack,
//   TextField,
//   Typography,
// } from "@mui/material";
// import { useEffect, useMemo, useState } from "react";
// import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
// import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// import {
//   fetchStudentLmsQuizResults,
//   fetchStudentLmsCourseFeedback,
//   fetchLmsQuizById,
//   LmsLesson,
//   submitLmsCourseFeedback,
//   submitLmsChapterQuiz,
//   submitLmsFinalQuiz,
//   useLmsCourseDetails,
//   useUpdateLmsProgress,
// } from "@/client/services/lms";
// import { useUser } from "@/client/services/user";

// const FINAL_QUIZ_CHAPTER_ID = "__final__";
// const FEEDBACK_SUGGESTIONS = [
//   "Content quality",
//   "Instructor clarity",
//   "Quiz quality",
//   "Platform experience",
//   "Need more examples",
// ];

// const mapResourceDownloadUrl = (rawUrl: string) => {
//   const value = String(rawUrl || "").trim();
//   if (!value) return value;
//   if (value.startsWith("/exam/api/lms/resources/")) {
//     return value.replace("/exam/api/lms", "/api/integration/external-service/lms");
//   }
//   if (value.startsWith("/integration/external-service/lms/resources/")) {
//     return `/api${value}`;
//   }
//   return value;
// };

// const downloadFile = async (rawUrl: string, filename?: string) => {
//   const url = mapResourceDownloadUrl(rawUrl);
//   if (!url) return;
//   try {
//     const response = await fetch(url);
//     if (!response.ok) {
//       // Fallback: open in new tab if fetch fails
//       window.open(url, "_blank", "noopener,noreferrer");
//       return;
//     }
//     const blob = await response.blob();
//     const blobUrl = URL.createObjectURL(blob);
//     const anchor = document.createElement("a");
//     anchor.href = blobUrl;
//     // Derive filename from URL path or use provided name
//     const derivedName = filename || url.split("/").pop()?.split("?")[0] || "download";
//     anchor.download = derivedName;
//     document.body.appendChild(anchor);
//     anchor.click();
//     document.body.removeChild(anchor);
//     URL.revokeObjectURL(blobUrl);
//   } catch {
//     // Fallback: open in new tab
//     window.open(url, "_blank", "noopener,noreferrer");
//   }
// };

// type QuizResultView = {
//   label: string;
//   status: string;
//   answersMap: Record<string, number>;
// };

// const toQuizLabel = (percentage: unknown) => {
//   const parsed = Number(percentage);
//   return Number.isFinite(parsed) ? `${parsed}%` : "Completed";
// };

// // const resolveYoutubeEmbed = (videoUrl?: string) => {
// //   if (!videoUrl) return null;
// //   const match = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
// //   if (!match) return null;
// //   return `https://www.youtube.com/embed/${match[1]}?rel=0&modestbranding=1`;
// // };

// const resolveYoutubeEmbed = (videoUrl?: string) => {
//   if (!videoUrl) return null;
//   const match = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
//   if (!match) return null;
//   const origin =
//     typeof window !== "undefined" && window.location?.origin
//       ? `&origin=${encodeURIComponent(window.location.origin)}`
//       : "";
//   return `https://www.youtube.com/embed/${match[1]}?rel=0&modestbranding=1${origin}`;
// };

// const isExternalLesson = (lesson?: LmsLesson) => {
//   if (!lesson) return false;
//   return lesson.videoSourceType === "external";
// };

// const resolveMeetingProvider = (videoUrl?: string) => {
//   const url = String(videoUrl || "").toLowerCase();
//   if (url.includes("zoom.us")) return "zoom";
//   if (url.includes("teams.microsoft.com")) return "teams";
//   if (url.includes("meet.google.com")) return "google-meet";
//   return "external";
// };

// const resolveVideoMimeType = (videoUrl?: string) => {
//   const path = String(videoUrl || "")
//     .split("?")[0]
//     .toLowerCase();
//   if (path.endsWith(".m3u8")) return "application/vnd.apple.mpegurl";
//   if (path.endsWith(".mpd")) return "application/dash+xml";
//   if (path.endsWith(".webm")) return "video/webm";
//   if (path.endsWith(".ogg") || path.endsWith(".ogv")) return "video/ogg";
//   return "video/mp4";
// };

// const LmsSmartVideoPlayer = ({ lesson }: { lesson: LmsLesson }) => {
//   const [isWaiting, setIsWaiting] = useState(false);
//   const [hasError, setHasError] = useState(false);
//   const videoUrl = String(lesson.videoUrl || "").trim();
//   const mimeType = resolveVideoMimeType(videoUrl);

//   useEffect(() => {
//     setIsWaiting(false);
//     setHasError(false);
//   }, [lesson.id, videoUrl]);

//   if (!videoUrl) {
//     return (
//       <Stack
//         sx={{
//           width: "100%",
//           aspectRatio: "16 / 9",
//           borderRadius: 2,
//           bgcolor: "black",
//           color: "#f8fafc",
//           alignItems: "center",
//           justifyContent: "center",
//           textAlign: "center",
//           p: 3,
//         }}
//       >
//         <Typography>{t`Video is not available for this lesson.`}</Typography>
//       </Stack>
//     );
//   }

//   return (
//     <Box
//       sx={{
//         position: "relative",
//         width: "100%",
//         aspectRatio: "16 / 9",
//         bgcolor: "black",
//         borderRadius: 2,
//       }}
//     >
//       <Box
//         key={`${lesson.id}-${videoUrl}`}
//         component="video"
//         controls
//         controlsList="nodownload"
//         playsInline
//         preload="metadata"
//         sx={{
//           display: "block",
//           width: "100%",
//           height: "100%",
//           borderRadius: 2,
//           bgcolor: "black",
//           objectFit: "contain",
//         }}
//         onLoadStart={() => {
//           setIsWaiting(true);
//           setHasError(false);
//         }}
//         onLoadedMetadata={() => setIsWaiting(false)}
//         onCanPlay={() => setIsWaiting(false)}
//         onPlaying={() => setIsWaiting(false)}
//         onWaiting={() => setIsWaiting(true)}
//         onError={() => {
//           setIsWaiting(false);
//           setHasError(true);
//         }}
//       >
//         <source src={videoUrl} type={mimeType} />
//       </Box>

//       {isWaiting ? (
//         <Stack
//           direction="row"
//           spacing={1}
//           alignItems="center"
//           sx={{
//             position: "absolute",
//             left: 16,
//             bottom: 16,
//             px: 1.25,
//             py: 0.75,
//             borderRadius: 1,
//             bgcolor: "rgba(15, 23, 42, 0.82)",
//             color: "#f8fafc",
//           }}
//         >
//           <CircularProgress size={16} color="inherit" />
//           <Typography variant="caption">{t`Preparing video...`}</Typography>
//         </Stack>
//       ) : null}

//       {hasError ? (
//         <Stack
//           spacing={0.5}
//           sx={{
//             position: "absolute",
//             inset: 0,
//             alignItems: "center",
//             justifyContent: "center",
//             textAlign: "center",
//             color: "#f8fafc",
//             bgcolor: "rgba(15, 23, 42, 0.88)",
//             borderRadius: 2,
//             p: 3,
//           }}
//         >
//           <Typography fontWeight={700}>{t`Unable to play this video.`}</Typography>
//           <Typography variant="body2" color="rgba(248,250,252,0.8)">
//             {t`Please try again, or contact support if the issue continues.`}
//           </Typography>
//         </Stack>
//       ) : null}
//     </Box>
//   );
// };

// const LmsPlayerPage = () => {
//   const { courseId = "" } = useParams();
//   const navigate = useNavigate();
//   const [searchParams] = useSearchParams();
//   const { data: course, isLoading } = useLmsCourseDetails(courseId);
//   const { user } = useUser();
//   const updateProgress = useUpdateLmsProgress();

//   const lessons = useMemo(
//     () => course?.chapters.flatMap((chapter) => chapter.lessons) ?? [],
//     [course?.chapters],
//   );
//   const [activeLessonId, setActiveLessonId] = useState<string>("");
//   const [completedLessonIds, setCompletedLessonIds] = useState<string[]>([]);
//   const [quizResultsByChapter, setQuizResultsByChapter] = useState<Record<string, QuizResultView>>(
//     {},
//   );
//   const [quizDialogOpen, setQuizDialogOpen] = useState(false);
//   const [activeQuizChapterId, setActiveQuizChapterId] = useState("");
//   const [activeQuiz, setActiveQuiz] = useState<any>(null);
//   const [resourcesDialogOpen, setResourcesDialogOpen] = useState(false);
//   const [activeResourcesChapterId, setActiveResourcesChapterId] = useState("");
//   const [activeResourcesTitle, setActiveResourcesTitle] = useState("");
//   const [activeResources, setActiveResources] = useState<
//     Array<{
//       id: string;
//       type: "url" | "text" | "file";
//       title?: string;
//       value: string;
//     }>
//   >([]);
//   const [quizAnswers, setQuizAnswers] = useState<Record<string, number>>({});
//   const [submittingQuiz, setSubmittingQuiz] = useState(false);
//   const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
//   const [feedbackRating, setFeedbackRating] = useState<number | null>(0);
//   const [feedbackText, setFeedbackText] = useState("");
//   const [feedbackSuggestions, setFeedbackSuggestions] = useState<string[]>([]);
//   const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
//   const [feedbackStatus, setFeedbackStatus] = useState<"submitted" | "skipped" | null>(null);
//   const [feedbackReadOnly, setFeedbackReadOnly] = useState(false);

//   useEffect(() => {
//     const run = async () => {
//       if (!courseId || !user?.email || !user?.id) return;
//       try {
//         const rows = await fetchStudentLmsQuizResults(courseId);
//         const mapped: Record<string, QuizResultView> = {};
//         rows.forEach((row) => {
//           const rawAnswers = Array.isArray((row.rawResult as any)?.answers)
//             ? (row.rawResult as any).answers
//             : [];
//           const answersMap: Record<string, number> = {};
//           rawAnswers.forEach((answer: any) => {
//             const qid = String(answer?.questionId || "").trim();
//             if (!qid) return;
//             answersMap[qid] = Number(answer?.selectedOptionIndex ?? -1);
//           });
//           mapped[row.chapterId] = {
//             label: toQuizLabel(row.percentage),
//             status: String(row.status || ""),
//             answersMap,
//           };
//         });
//         setQuizResultsByChapter(mapped);
//       } catch {
//         setQuizResultsByChapter({});
//       }
//     };
//     run();
//   }, [courseId, user?.email, user?.id]);

//   useEffect(() => {
//     const run = async () => {
//       if (!courseId || !user?.id) return;
//       try {
//         const existing = await fetchStudentLmsCourseFeedback(courseId);
//         if (!existing) return;
//         setFeedbackStatus(existing.status === "skipped" ? "skipped" : "submitted");
//         setFeedbackRating(Number(existing.rating ?? 0));
//         setFeedbackText(String(existing.feedback || ""));
//         setFeedbackSuggestions(Array.isArray(existing.suggestions) ? existing.suggestions : []);
//       } catch {
//         // Ignore feedback fetch errors on player load.
//       }
//     };
//     run();
//   }, [courseId, user?.id]);

//   useEffect(() => {
//     const rows = (course?.completedLessonIds || []).map((id) => String(id));
//     setCompletedLessonIds(rows);
//   }, [course?.completedLessonIds]);

//   const completedSet = useMemo(() => new Set(completedLessonIds), [completedLessonIds]);
//   const requestedLessonId = useMemo(
//     () => String(searchParams.get("lessonId") || "").trim(),
//     [searchParams],
//   );

//   const chapterFlow = useMemo(() => {
//     const chapters = course?.chapters || [];
//     const lessonState: Array<{
//       lesson: LmsLesson;
//       chapterId: string;
//       chapterIndex: number;
//       lessonIndex: number;
//       unlocked: boolean;
//       completed: boolean;
//     }> = [];
//     const chapterState: Array<{
//       id: string;
//       unlocked: boolean;
//       lessonsCompleted: boolean;
//       quizRequired: boolean;
//       quizCompleted: boolean;
//       completed: boolean;
//       quizUnlocked: boolean;
//     }> = [];

//     let allPreviousChaptersCompleted = true;
//     chapters.forEach((chapter, chapterIndex) => {
//       const lessons = chapter.lessons || [];
//       const lessonsCompleted = lessons.every((lesson) => completedSet.has(String(lesson.id)));
//       const quizRequired = Boolean(chapter.quizEnabled && chapter.quizId);
//       const quizCompleted =
//         String(quizResultsByChapter[chapter.id]?.status || "").toLowerCase() === "completed";
//       const completed = lessonsCompleted && (!quizRequired || quizCompleted);
//       const unlocked = chapterIndex === 0 ? true : allPreviousChaptersCompleted;
//       const quizUnlocked = unlocked && lessonsCompleted;

//       lessons.forEach((lesson, lessonIndex) => {
//         const previousLessonId = lessonIndex > 0 ? lessons[lessonIndex - 1]?.id : "";
//         const lessonUnlocked =
//           unlocked && (lessonIndex === 0 || completedSet.has(String(previousLessonId)));
//         lessonState.push({
//           lesson,
//           chapterId: chapter.id,
//           chapterIndex,
//           lessonIndex,
//           unlocked: lessonUnlocked,
//           completed: completedSet.has(String(lesson.id)),
//         });
//       });

//       chapterState.push({
//         id: chapter.id,
//         unlocked,
//         lessonsCompleted,
//         quizRequired,
//         quizCompleted,
//         completed,
//         quizUnlocked,
//       });

//       allPreviousChaptersCompleted = allPreviousChaptersCompleted && completed;
//     });

//     const allChaptersCompleted = chapterState.every((chapter) => chapter.completed);
//     const finalQuizRequired = Boolean(course?.finalQuizEnabled && course?.finalQuizId);
//     const finalQuizCompleted =
//       String(quizResultsByChapter[FINAL_QUIZ_CHAPTER_ID]?.status || "").toLowerCase() ===
//       "completed";
//     const finalQuizUnlocked = allChaptersCompleted;
//     const courseCompleted = allChaptersCompleted && (!finalQuizRequired || finalQuizCompleted);

//     return {
//       lessonState,
//       chapterState,
//       allChaptersCompleted,
//       finalQuizRequired,
//       finalQuizCompleted,
//       finalQuizUnlocked,
//       courseCompleted,
//     };
//   }, [
//     completedSet,
//     course?.chapters,
//     course?.finalQuizEnabled,
//     course?.finalQuizId,
//     quizResultsByChapter,
//   ]);

//   const lessonStateMap = useMemo(() => {
//     const map = new Map<string, (typeof chapterFlow.lessonState)[number]>();
//     chapterFlow.lessonState.forEach((item) => map.set(String(item.lesson.id), item));
//     return map;
//   }, [chapterFlow.lessonState]);

//   const activeLesson = useMemo(() => {
//     if (chapterFlow.lessonState.length === 0) return undefined;
//     const found = chapterFlow.lessonState.find(
//       (item) => String(item.lesson.id) === activeLessonId && item.unlocked,
//     );
//     if (found) return found.lesson;
//     return (
//       chapterFlow.lessonState.find((item) => item.unlocked)?.lesson ??
//       chapterFlow.lessonState[0].lesson
//     );
//   }, [activeLessonId, chapterFlow.lessonState]);

//   useEffect(() => {
//     if (!requestedLessonId) return;
//     const canOpenRequestedLesson = chapterFlow.lessonState.some(
//       (item) => String(item.lesson.id) === requestedLessonId && item.unlocked,
//     );
//     if (!canOpenRequestedLesson) return;
//     setActiveLessonId((prev) => (prev ? prev : requestedLessonId));
//   }, [chapterFlow.lessonState, requestedLessonId]);

//   const activeIndex = useMemo(
//     () =>
//       chapterFlow.lessonState.findIndex(
//         (item) => String(item.lesson.id) === String(activeLesson?.id),
//       ),
//     [activeLesson?.id, chapterFlow.lessonState],
//   );
//   const prevLesson = activeIndex > 0 ? chapterFlow.lessonState[activeIndex - 1]?.lesson : undefined;
//   const nextLessonState = activeIndex >= 0 ? chapterFlow.lessonState[activeIndex + 1] : undefined;
//   const nextLesson = nextLessonState?.unlocked ? nextLessonState.lesson : undefined;
//   const activeLessonState = activeLesson ? lessonStateMap.get(String(activeLesson.id)) : undefined;

//   const selectLesson = (lesson: LmsLesson) => {
//     const state = lessonStateMap.get(String(lesson.id));
//     if (!state?.unlocked) return;
//     setActiveLessonId(lesson.id);
//   };

//   const markComplete = async () => {
//     if (!activeLesson || !course) return;
//     await updateProgress.mutateAsync({
//       courseId: course.id,
//       lessonId: activeLesson.id,
//       watchedPercent: 100,
//     });
//     setCompletedLessonIds((prev) =>
//       prev.includes(activeLesson.id) ? prev : [...prev, activeLesson.id],
//     );
//     await handleNext();
//   };

//   const handleNext = async () => {
//     if (!course || !activeLesson || !activeLessonState) return;
//     const chapterIndex = activeLessonState.chapterIndex;
//     const chapter = course.chapters[chapterIndex];
//     if (!chapter) return;

//     const isLastLessonInChapter =
//       activeLessonState.lessonIndex >= (chapter.lessons?.length || 0) - 1;
//     if (!isLastLessonInChapter) {
//       if (nextLesson) {
//         selectLesson(nextLesson);
//       }
//       return;
//     }

//     if (
//       chapter.resourcesEnabled &&
//       (chapter.resources || []).some((item) => String(item.value || "").trim())
//     ) {
//       openChapterResources(chapter.id);
//       return;
//     }

//     if (chapter.quizEnabled && chapter.quizId) {
//       await openChapterQuiz(chapter.id, chapter.quizId);
//       return;
//     }

//     const nextChapter = course.chapters[chapterIndex + 1];
//     if (nextChapter?.lessons?.length) {
//       selectLesson(nextChapter.lessons[0]);
//       return;
//     }

//     if (course.finalQuizEnabled && course.finalQuizId) {
//       await openChapterQuiz(FINAL_QUIZ_CHAPTER_ID, course.finalQuizId);
//     }
//   };
//   const moveForwardFromChapterQuiz = async (chapterId: string) => {
//     if (!course) return;
//     const chapterIndex = (course.chapters || []).findIndex(
//       (chapter) => String(chapter.id) === String(chapterId),
//     );
//     if (chapterIndex < 0) return;
//     const nextChapter = course.chapters[chapterIndex + 1];
//     if (nextChapter?.lessons?.length) {
//       setActiveLessonId(String(nextChapter.lessons[0].id));
//       return;
//     }
//     if (course.finalQuizEnabled && course.finalQuizId) {
//       await openChapterQuiz(FINAL_QUIZ_CHAPTER_ID, course.finalQuizId);
//     }
//   };

//   const moveForwardFromChapterResources = async (chapterId: string) => {
//     if (!course) return;
//     const chapterIndex = (course.chapters || []).findIndex(
//       (chapter) => String(chapter.id) === String(chapterId),
//     );
//     if (chapterIndex < 0) return;
//     const chapter = course.chapters[chapterIndex];
//     if (chapter?.quizEnabled && chapter.quizId) {
//       await openChapterQuiz(chapter.id, chapter.quizId);
//       return;
//     }
//     const nextChapter = course.chapters[chapterIndex + 1];
//     if (nextChapter?.lessons?.length) {
//       setActiveLessonId(String(nextChapter.lessons[0].id));
//       return;
//     }
//     if (course.finalQuizEnabled && course.finalQuizId) {
//       await openChapterQuiz(FINAL_QUIZ_CHAPTER_ID, course.finalQuizId);
//     }
//   };

//   if (isLoading) {
//     return (
//       <Stack alignItems="center" py={8}>
//         <CircularProgress />
//       </Stack>
//     );
//   }

//   if (!course || !activeLesson) {
//     return (
//       <Container>
//         <Typography>{t`Unable to load the course player.`}</Typography>
//       </Container>
//     );
//   }

//   const youtubeEmbedUrl = resolveYoutubeEmbed(activeLesson.videoUrl);
//   const externalLesson = isExternalLesson(activeLesson);
//   const provider = resolveMeetingProvider(activeLesson.videoUrl);
//   const providerTitle =
//     provider === "zoom"
//       ? t`Zoom Meeting`
//       : provider === "teams"
//         ? t`Microsoft Teams Meeting`
//         : provider === "google-meet"
//           ? t`Google Meet`
//           : t`Live Class Link`;
//   const providerCta =
//     provider === "zoom"
//       ? t`Join Zoom Meeting`
//       : provider === "teams"
//         ? t`Join Teams Meeting`
//         : provider === "google-meet"
//           ? t`Join Google Meet`
//           : t`Join Live Class`;
//   const isActiveLessonCompleted = completedLessonIds.includes(activeLesson.id);

//   const openChapterQuiz = async (chapterId: string, quizId: string) => {
//     if (!quizId) return;
//     const quiz = await fetchLmsQuizById(quizId);
//     setActiveQuizChapterId(chapterId);
//     setActiveQuiz(quiz);
//     const savedAnswers = quizResultsByChapter[chapterId]?.answersMap || {};
//     setQuizAnswers(savedAnswers);
//     setQuizDialogOpen(true);
//   };

//   const openChapterResources = (chapterId: string) => {
//     const chapter = (course?.chapters || []).find((item) => String(item.id) === String(chapterId));
//     if (!chapter || !chapter.resourcesEnabled) return;
//     const resources = (chapter.resources || []).filter((item) => String(item.value || "").trim());
//     if (resources.length === 0) return;

//     setActiveResourcesChapterId(String(chapter.id));
//     setActiveResourcesTitle(String(chapter.resourcesTitle || chapter.title || "").trim());
//     setActiveResources(resources);
//     setResourcesDialogOpen(true);
//   };

//   const submitQuiz = async () => {
//     if (!course || !activeQuizChapterId || !activeQuiz) return;
//     if (
//       String(quizResultsByChapter[activeQuizChapterId]?.status || "").toLowerCase() === "completed"
//     ) {
//       setQuizDialogOpen(false);
//       return;
//     }
//     const answers = (activeQuiz.questions || []).map((question: any) => ({
//       questionId: question.id,
//       selectedOptionIndex: Number(quizAnswers[question.id] ?? -1),
//     }));
//     setSubmittingQuiz(true);
//     try {
//       if (activeQuizChapterId === FINAL_QUIZ_CHAPTER_ID) {
//         await submitLmsFinalQuiz({
//           courseId: course.id,
//           answers,
//         });
//       } else {
//         await submitLmsChapterQuiz({
//           courseId: course.id,
//           chapterId: activeQuizChapterId,
//           answers,
//         });
//       }
//       const rows = await fetchStudentLmsQuizResults(course.id);
//       const mapped: Record<string, QuizResultView> = {};
//       rows.forEach((row) => {
//         const rawAnswers = Array.isArray((row.rawResult as any)?.answers)
//           ? (row.rawResult as any).answers
//           : [];
//         const answersMap: Record<string, number> = {};
//         rawAnswers.forEach((answer: any) => {
//           const qid = String(answer?.questionId || "").trim();
//           if (!qid) return;
//           answersMap[qid] = Number(answer?.selectedOptionIndex ?? -1);
//         });
//         mapped[row.chapterId] = {
//           label: toQuizLabel(row.percentage),
//           status: String(row.status || ""),
//           answersMap,
//         };
//       });
//       setQuizResultsByChapter(mapped);
//       setQuizDialogOpen(false);
//       if (activeQuizChapterId === FINAL_QUIZ_CHAPTER_ID && !feedbackStatus) {
//         setFeedbackReadOnly(false);
//         setFeedbackDialogOpen(true);
//       }

//       if (activeQuizChapterId !== FINAL_QUIZ_CHAPTER_ID) {
//         const chapterIndex = (course.chapters || []).findIndex(
//           (chapter) => String(chapter.id) === String(activeQuizChapterId),
//         );
//         const nextChapter = chapterIndex >= 0 ? course.chapters[chapterIndex + 1] : undefined;
//         if (nextChapter?.lessons?.length) {
//           setActiveLessonId(String(nextChapter.lessons[0].id));
//         } else if (course.finalQuizEnabled && course.finalQuizId) {
//           await openChapterQuiz(FINAL_QUIZ_CHAPTER_ID, course.finalQuizId);
//         }
//       }
//     } finally {
//       setSubmittingQuiz(false);
//     }
//   };
//   const activeQuizCompleted =
//     String(quizResultsByChapter[activeQuizChapterId]?.status || "").toLowerCase() === "completed";
//   const canGoNextFromQuizPopup = (() => {
//     if (!activeQuizCompleted) return false;
//     if (!course || activeQuizChapterId === FINAL_QUIZ_CHAPTER_ID) return false;
//     const chapterIndex = (course.chapters || []).findIndex(
//       (chapter) => String(chapter.id) === String(activeQuizChapterId),
//     );
//     if (chapterIndex < 0) return false;
//     const hasNextChapterLesson = Boolean(course.chapters[chapterIndex + 1]?.lessons?.length);
//     const hasFinalQuiz = Boolean(course.finalQuizEnabled && course.finalQuizId);
//     return hasNextChapterLesson || hasFinalQuiz;
//   })();
//   const canGoNextFromResourcesPopup = (() => {
//     if (!course || !activeResourcesChapterId) return false;
//     const chapterIndex = (course.chapters || []).findIndex(
//       (chapter) => String(chapter.id) === String(activeResourcesChapterId),
//     );
//     if (chapterIndex < 0) return false;
//     const hasChapterQuiz = Boolean(
//       course.chapters[chapterIndex]?.quizEnabled && course.chapters[chapterIndex]?.quizId,
//     );
//     const hasNextChapterLesson = Boolean(course.chapters[chapterIndex + 1]?.lessons?.length);
//     const hasFinalQuiz = Boolean(course.finalQuizEnabled && course.finalQuizId);
//     return hasChapterQuiz || hasNextChapterLesson || hasFinalQuiz;
//   })();

//   const toggleFeedbackSuggestion = (value: string) => {
//     setFeedbackSuggestions((prev) =>
//       prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value],
//     );
//   };

//   const submitCourseFeedback = async () => {
//     if (!course) return;
//     if (feedbackStatus === "submitted") return;
//     const rating = Number(feedbackRating ?? 0);
//     if (rating < 1 || rating > 5) return;

//     setFeedbackSubmitting(true);
//     try {
//       await submitLmsCourseFeedback({
//         courseId: course.id,
//         status: "submitted",
//         rating,
//         feedback: feedbackText,
//         suggestions: feedbackSuggestions,
//       });
//       setFeedbackStatus("submitted");
//       setFeedbackReadOnly(true);
//       setFeedbackDialogOpen(false);
//     } finally {
//       setFeedbackSubmitting(false);
//     }
//   };

//   const skipCourseFeedback = async () => {
//     if (!course) return;
//     if (feedbackStatus === "submitted") {
//       setFeedbackDialogOpen(false);
//       return;
//     }
//     setFeedbackSubmitting(true);
//     try {
//       await submitLmsCourseFeedback({
//         courseId: course.id,
//         status: "skipped",
//         rating: 0,
//         feedback: "",
//         suggestions: [],
//       });
//       setFeedbackStatus("skipped");
//       setFeedbackDialogOpen(false);
//     } finally {
//       setFeedbackSubmitting(false);
//     }
//   };
//   const handleGoBack = () => {
//     if (window.history.length > 1) {
//       navigate(-1);
//       return;
//     }
//     navigate("/lms");
//   };

//   return (
//     <Box>
//       <Container maxWidth={false}>
//         <Stack spacing={2}>
//           <Box>
//             <Button
//               startIcon={<ArrowBackIcon />}
//               size="small"
//               onClick={handleGoBack}
//               sx={{ textTransform: "none" }}
//             >
//               {t`Back`}
//             </Button>
//           </Box>
//           <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
//             <Box sx={{ flex: 1 }}>
//               <Stack spacing={2}>
//                 <Box
//                   sx={{
//                     position: { xs: "static", lg: "sticky" },
//                     top: { lg: 56 },
//                     zIndex: 4,
//                     bgcolor: "background.paper",
//                     borderRadius: 2,
//                     mb: 1,
//                   }}
//                 >
//                   {youtubeEmbedUrl ? (
//                     <Box
//                       component="iframe"
//                       src={youtubeEmbedUrl}
//                       title={activeLesson.title}
//                       sx={{
//                         width: "100%",
//                         aspectRatio: "16 / 9",
//                         border: 0,
//                         borderRadius: 2,
//                         bgcolor: "black",
//                       }}
//                       allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
//                       allowFullScreen
//                     />
//                   ) : externalLesson ? (
//                     <Stack
//                       sx={{
//                         width: "100%",
//                         aspectRatio: "16 / 9",
//                         borderRadius: 2,
//                         bgcolor: "#0f172a",
//                         color: "#f8fafc",
//                         p: 3,
//                         alignItems: "center",
//                         justifyContent: "center",
//                         textAlign: "center",
//                       }}
//                       spacing={1.5}
//                     >
//                       <Typography variant="h6" fontWeight={700}>
//                         {providerTitle}
//                       </Typography>
//                       <Typography color="rgba(248,250,252,0.85)">
//                         {t`This lesson uses an external meeting link.`}
//                       </Typography>
//                       <Button
//                         component={MuiLink}
//                         href={activeLesson.videoUrl || "#"}
//                         target="_blank"
//                         rel="noopener noreferrer"
//                         variant="contained"
//                         color="success"
//                         disabled={!activeLesson.videoUrl}
//                       >
//                         {providerCta}
//                       </Button>
//                     </Stack>
//                   ) : (
//                     <LmsSmartVideoPlayer lesson={activeLesson} />
//                   )}
//                 </Box>

//                 <Stack spacing={1} sx={{ position: "relative", zIndex: 2, pt: 0.5 }}>
//                   <Typography variant="h6" fontWeight={700}>
//                     {activeLesson.title}
//                   </Typography>
//                   <Typography color="text.secondary">
//                     {/* {activeLesson.description || t`Lesson details can be added from the admin builder.`} */}
//                   </Typography>
//                 </Stack>

//                 <Stack direction="row" spacing={1} flexWrap="wrap">
//                   <Button
//                     variant="outlined"
//                     startIcon={<NavigateBeforeOutlinedIcon />}
//                     disabled={!prevLesson}
//                     onClick={() => prevLesson && selectLesson(prevLesson)}
//                   >
//                     {t`Previous`}
//                   </Button>
//                   <Button
//                     variant="outlined"
//                     endIcon={<NavigateNextOutlinedIcon />}
//                     disabled={!activeLessonState || !isActiveLessonCompleted}
//                     onClick={handleNext}
//                   >
//                     {t`Next`}
//                   </Button>
//                   <Button
//                     variant="contained"
//                     startIcon={<TaskAltOutlinedIcon />}
//                     onClick={markComplete}
//                     disabled={updateProgress.isPending || isActiveLessonCompleted}
//                   >
//                     {updateProgress.isPending
//                       ? t`Updating...`
//                       : isActiveLessonCompleted
//                         ? t`Completed`
//                         : t`Mark Complete`}
//                   </Button>
//                   <Button component={Link} to={`/lms/${course.id}`}>
//                     {t`Course Overview`}
//                   </Button>
//                 </Stack>
//               </Stack>
//             </Box>

//             <Paper
//               variant="outlined"
//               sx={{
//                 width: { xs: "100%", lg: 360 },
//                 maxHeight: { lg: "calc(100vh - 95px)" },
//                 overflow: "auto",
//                 borderRadius: 2,
//               }}
//             >
//               {course.chapters.map((chapter, chapterIndex) => {
//                 const chapterStatus = chapterFlow.chapterState[chapterIndex];
//                 const chapterLocked = !chapterStatus?.unlocked;
//                 return (
//                   <Box key={chapter.id}>
//                     <Stack sx={{ px: 2, py: 1.2, bgcolor: "#f8fafc" }}>
//                       <Typography
//                         sx={{
//                           fontWeight: 700,
//                           color: chapterLocked ? "text.disabled" : "text.primary",
//                         }}
//                       >
//                         {chapter.title}
//                       </Typography>
//                     </Stack>
//                     <List disablePadding>
//                       {chapter.lessons.map((lesson) =>
//                         (() => {
//                           const lessonState = lessonStateMap.get(String(lesson.id));
//                           return (
//                             <ListItemButton
//                               key={lesson.id}
//                               selected={lesson.id === activeLesson.id}
//                               disabled={!lessonState?.unlocked}
//                               onClick={() => selectLesson(lesson)}
//                             >
//                               <ListItemText
//                                 primary={lesson.title}
//                                 secondary={
//                                   lessonState?.completed
//                                     ? `${lesson.durationInMinutes ?? 0} ${t`mins`} | ${t`Completed`}`
//                                     : lessonState?.unlocked
//                                       ? `${lesson.durationInMinutes ?? 0} ${t`mins`}`
//                                       : t`Locked`
//                                 }
//                                 primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
//                               />
//                             </ListItemButton>
//                           );
//                         })(),
//                       )}
//                       {chapter.resourcesEnabled &&
//                       (chapter.resources || []).some((item) => String(item.value || "").trim()) ? (
//                         <ListItemButton
//                           disabled={!chapterStatus?.quizUnlocked}
//                           onClick={() => openChapterResources(chapter.id)}
//                         >
//                           <Stack
//                             direction="row"
//                             alignItems="center"
//                             justifyContent="space-between"
//                             width="100%"
//                           >
//                             <ListItemText
//                               primary={`${chapter.title} - ${chapter.resourcesTitle || t`Resources`}`}
//                               secondary={chapterStatus?.quizUnlocked ? t`Available` : t`Locked`}
//                               primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
//                             />
//                           </Stack>
//                         </ListItemButton>
//                       ) : null}
//                       {chapter.quizEnabled && chapter.quizId ? (
//                         <ListItemButton
//                           disabled={!chapterStatus?.quizUnlocked}
//                           onClick={() => openChapterQuiz(chapter.id, chapter.quizId || "")}
//                         >
//                           <Stack
//                             direction="row"
//                             alignItems="center"
//                             justifyContent="space-between"
//                             width="100%"
//                           >
//                             <ListItemText
//                               primary={`${chapter.title} - ${t`Quiz`}`}
//                               secondary={
//                                 quizResultsByChapter[chapter.id]
//                                   ? `${t`Completed`}: ${quizResultsByChapter[chapter.id].label}`
//                                   : chapterStatus?.quizUnlocked
//                                     ? t`Pending`
//                                     : t`Locked`
//                               }
//                               primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
//                             />
//                             {String(
//                               quizResultsByChapter[chapter.id]?.status || "",
//                             ).toLowerCase() === "completed" ? (
//                               <Button
//                                 size="small"
//                                 variant="outlined"
//                                 onClick={(event) => {
//                                   event.stopPropagation();
//                                   moveForwardFromChapterQuiz(chapter.id);
//                                 }}
//                               >
//                                 {t`Next`}
//                               </Button>
//                             ) : null}
//                           </Stack>
//                         </ListItemButton>
//                       ) : null}
//                     </List>
//                   </Box>
//                 );
//               })}
//               {course.finalQuizEnabled && course.finalQuizId ? (
//                 <Box sx={{ borderTop: "1px solid #e2e8f0", p: 2 }}>
//                   <List disablePadding>
//                     <ListItemButton
//                       disabled={!chapterFlow.finalQuizUnlocked}
//                       onClick={() =>
//                         openChapterQuiz(FINAL_QUIZ_CHAPTER_ID, course.finalQuizId || "")
//                       }
//                     >
//                       <ListItemText
//                         primary={course.finalQuizTitle || t`Final Quiz`}
//                         secondary={
//                           quizResultsByChapter[FINAL_QUIZ_CHAPTER_ID]
//                             ? `${t`Completed`}: ${quizResultsByChapter[FINAL_QUIZ_CHAPTER_ID].label}`
//                             : chapterFlow.finalQuizUnlocked
//                               ? t`Pending`
//                               : t`Locked`
//                         }
//                         primaryTypographyProps={{ fontSize: 14, fontWeight: 700 }}
//                       />
//                     </ListItemButton>
//                   </List>
//                 </Box>
//               ) : null}
//               <Box sx={{ p: 2, borderTop: "1px solid #e2e8f0" }}>
//                 <Typography
//                   variant="caption"
//                   color={chapterFlow.courseCompleted ? "success.main" : "text.secondary"}
//                 >
//                   {chapterFlow.courseCompleted
//                     ? t`Course completed`
//                     : t`Complete all lessons and required quizzes to unlock all content`}
//                 </Typography>
//               </Box>
//             </Paper>
//           </Stack>
//         </Stack>
//       </Container>

//       <Dialog
//         open={quizDialogOpen}
//         onClose={() => setQuizDialogOpen(false)}
//         fullWidth
//         maxWidth="md"
//       >
//         <DialogTitle>{activeQuiz?.title || t`Chapter Quiz`}</DialogTitle>
//         <DialogContent>
//           <Stack spacing={2} sx={{ mt: 1 }}>
//             {(activeQuiz?.questions || []).map((question: any, index: number) => (
//               <Paper key={question.id} variant="outlined" sx={{ p: 1.5 }}>
//                 <Typography fontWeight={600} mb={1}>
//                   {index + 1}. {question.question}
//                 </Typography>
//                 <TextField
//                   select
//                   fullWidth
//                   label={t`Select Answer`}
//                   value={quizAnswers[question.id] ?? ""}
//                   disabled={activeQuizCompleted}
//                   onChange={(event) =>
//                     setQuizAnswers((prev) => ({
//                       ...prev,
//                       [question.id]: Number(event.target.value),
//                     }))
//                   }
//                 >
//                   {question.options.map((option: string, optionIndex: number) => (
//                     <MenuItem key={`${question.id}-${optionIndex}`} value={optionIndex}>
//                       {option}
//                     </MenuItem>
//                   ))}
//                 </TextField>
//               </Paper>
//             ))}
//           </Stack>
//         </DialogContent>
//         <DialogActions>
//           <Button onClick={() => setQuizDialogOpen(false)}>{t`Cancel`}</Button>
//           {!activeQuizCompleted ? (
//             <Button onClick={submitQuiz} variant="contained" disabled={submittingQuiz}>
//               {submittingQuiz ? t`Submitting...` : t`Submit Quiz`}
//             </Button>
//           ) : (
//             <Stack direction="row" spacing={1}>
//               <Button variant="contained" disabled>
//                 {t`Quiz Completed`}
//               </Button>
//               {canGoNextFromQuizPopup ? (
//                 <Button
//                   variant="outlined"
//                   onClick={async () => {
//                     setQuizDialogOpen(false);
//                     await moveForwardFromChapterQuiz(activeQuizChapterId);
//                   }}
//                 >
//                   {t`Next`}
//                 </Button>
//               ) : null}
//             </Stack>
//           )}
//         </DialogActions>
//       </Dialog>

//       <Dialog
//         open={resourcesDialogOpen}
//         onClose={() => setResourcesDialogOpen(false)}
//         fullWidth
//         maxWidth="md"
//       >
//         <DialogTitle>{activeResourcesTitle || t`Chapter Resources`}</DialogTitle>
//         <DialogContent>
//           <Stack spacing={2} sx={{ mt: 1 }}>
//             {activeResources.map((resource) => (
//               <Paper key={resource.id} variant="outlined" sx={{ p: 1.5 }}>
//                 <Stack spacing={1}>
//                   <Typography fontWeight={700}>
//                     {resource.title ||
//                       (resource.type === "url"
//                         ? t`Resource Link`
//                         : resource.type === "file"
//                           ? t`Resource File`
//                           : t`Resource Note`)}
//                   </Typography>
//                   {resource.type === "file" ||
//                   (resource.type === "text" &&
//                     /^https?:\/\//i.test(String(resource.value || "").trim())) ? (
//                     <Stack spacing={1}>
//                       {String(resource.value || "")
//                         .split("\n")
//                         .map((entry) => entry.trim())
//                         .filter(Boolean)
//                         .map((fileUrl, index) => (
//                           <Button
//                             key={`${resource.id}-file-${index}`}
//                             variant="outlined"
//                             sx={{ alignSelf: "flex-start" }}
//                             onClick={() => downloadFile(fileUrl)}
//                           >
//                             {t`Download file`} {index + 1}
//                           </Button>
//                         ))}
//                     </Stack>
//                   ) : resource.type === "url" ? (
//                     <Button
//                       component={MuiLink}
//                       href={resource.value}
//                       target="_blank"
//                       rel="noopener noreferrer"
//                       variant="outlined"
//                       sx={{ alignSelf: "flex-start" }}
//                     >
//                       {resource.value}
//                     </Button>
//                   ) : (
//                     <Typography variant="body2" color="text.secondary">
//                       {resource.value}
//                     </Typography>
//                   )}
//                 </Stack>
//               </Paper>
//             ))}
//             {activeResources.length === 0 ? (
//               <Typography color="text.secondary">{t`No resources available.`}</Typography>
//             ) : null}
//           </Stack>
//         </DialogContent>
//         <DialogActions>
//           <Button onClick={() => setResourcesDialogOpen(false)}>{t`Close`}</Button>
//           {canGoNextFromResourcesPopup ? (
//             <Button
//               variant="contained"
//               onClick={async () => {
//                 setResourcesDialogOpen(false);
//                 await moveForwardFromChapterResources(activeResourcesChapterId);
//               }}
//             >
//               {t`Next`}
//             </Button>
//           ) : null}
//         </DialogActions>
//       </Dialog>

//       <Dialog
//         open={feedbackDialogOpen}
//         onClose={() => setFeedbackDialogOpen(false)}
//         fullWidth
//         maxWidth="sm"
//       >
//         <DialogTitle>{t`Rate this course`}</DialogTitle>
//         <DialogContent>
//           <Stack spacing={2} sx={{ mt: 1 }}>
//             <Stack spacing={0.5}>
//               <Typography variant="body2">{t`Rating`}</Typography>
//               <Rating
//                 value={feedbackRating}
//                 onChange={(_, value) => setFeedbackRating(value)}
//                 max={5}
//                 readOnly={feedbackReadOnly}
//               />
//             </Stack>

//             <Stack spacing={1}>
//               <Typography variant="body2">{t`What went well?`}</Typography>
//               <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
//                 {FEEDBACK_SUGGESTIONS.map((item) => (
//                   <Chip
//                     key={item}
//                     label={item}
//                     clickable
//                     disabled={feedbackReadOnly}
//                     color={feedbackSuggestions.includes(item) ? "primary" : "default"}
//                     variant={feedbackSuggestions.includes(item) ? "filled" : "outlined"}
//                     onClick={() => toggleFeedbackSuggestion(item)}
//                   />
//                 ))}
//               </Stack>
//             </Stack>

//             <TextField
//               multiline
//               minRows={4}
//               label={t`Feedback`}
//               value={feedbackText}
//               disabled={feedbackReadOnly}
//               onChange={(event) => setFeedbackText(event.target.value)}
//               placeholder={t`Share your feedback or suggestions`}
//               fullWidth
//             />
//           </Stack>
//         </DialogContent>
//         <DialogActions>
//           {feedbackReadOnly ? (
//             <Button onClick={() => setFeedbackDialogOpen(false)}>{t`Close`}</Button>
//           ) : (
//             <>
//               <Button onClick={skipCourseFeedback} disabled={feedbackSubmitting}>{t`Skip`}</Button>
//               <Button
//                 variant="contained"
//                 onClick={submitCourseFeedback}
//                 disabled={feedbackSubmitting || Number(feedbackRating ?? 0) < 1}
//               >
//                 {feedbackSubmitting ? t`Submitting...` : t`Submit Feedback`}
//               </Button>
//             </>
//           )}
//         </DialogActions>
//       </Dialog>
//     </Box>
//   );
// };

// export default LmsPlayerPage;
